import { formatAbn, formatAcn, ABN_INPUT_MAX_LEN, ACN_INPUT_MAX_LEN } from "../../../../Utils/companyNumber";
import { useEffect, useState } from "react";
import { Col, Label, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { leadData } from "../FormData/CrmFormData";
import TextField from "../../../common/TextField";
import MobileField from "../../../common/MobileField";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import SelectDatePicker from "../../../common/SelectDatePicker";
import { H3 } from "../../../../AbstractElements";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { createCRMLeadService, getCrmDDLService } from "../../../../Services/CRMAdminServices/CRMTaskListService";
import { getAllStagesService } from "../../../../Services/CRMAdminServices/CRMStagesService";
import { mbStages } from "../../../../Utils/leadStageType";

// MB Builder - adds a mortgage broking business as a lead for the Fintrack
// Business Builder programme. Same Tbl_CrmLeads record as any other lead, with
// the company details carried in CompanyName / ABN / ACN on Tbl_CrmLeadInfo.
export default function MBBuilder() {
    const [formData, setFormData] = useState<any>({ ...leadData });
    const [formErrors, setFormErrors] = useState<any>({});
    const [leadDropdownData, setLeadDropdownData] = useState<any>([]);
    const [stagesOptions, setStagesOptions] = useState<any[]>([]);
    const navigate = useSafeNavigate();

    const handleValueChange = (name: string, value: string | Date | null) => {
        setFormData((prevData: any) => ({ ...prevData, [name]: value }));
        setFormErrors((prevErrors: any) => ({ ...prevErrors, [name]: "" }));
    };

    const handleNameChange = (name: string, value: string | Date | null) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: name === "surname" ? (value as string)?.toUpperCase() : value,
        }));
        setFormErrors((prevErrors: any) => ({ ...prevErrors, [name]: "" }));
    };

    const { data: leadsDataDDL, isLoading: DDLloading } = useGetRequestQuery<any>(
        ["getCrmDDLService"],
        () => getCrmDDLService(),
        { enabled: true }
    );

    useEffect(() => {
        if (leadsDataDDL?.data && !DDLloading) {
            const ddl = { ...leadsDataDDL.data };
            if (Array.isArray(ddl.leadSources)) ddl.leadSources.sort((a: any, b: any) => (a.label || "").localeCompare(b.label || ""));
            if (Array.isArray(ddl.staff)) ddl.staff.sort((a: any, b: any) => (a.label || "").localeCompare(b.label || ""));
            setLeadDropdownData(ddl);
        }
    }, [leadsDataDDL, DDLloading]);

    const {
        data: allStagesDataResponse,
        isLoading: stagesLoading,
        isError: stagesIsError,
    } = useGetRequestQuery<any>(["getAllStagesService"], getAllStagesService, { enabled: true });

    useEffect(() => {
        if (allStagesDataResponse && !stagesIsError && !stagesLoading) {
            const rawStages = Array.isArray(allStagesDataResponse?.data?.data)
                ? allStagesDataResponse.data.data
                : Array.isArray(allStagesDataResponse?.data)
                    ? allStagesDataResponse.data
                    : [];

            const transformedStages = rawStages
                .filter((stage: any) => !stage.isDeleted)
                .map((stage: any) => ({
                    value: stage.stageId.toString(),
                    label: stage.name,
                    orderBy: stage.orderBy,
                }));

            const sorted = transformedStages.sort((a: any, b: any) => {
                const orderA = a.orderBy !== undefined && a.orderBy !== null ? Number(a.orderBy) : 999;
                const orderB = b.orderBy !== undefined && b.orderBy !== null ? Number(b.orderBy) : 999;
                return orderA - orderB;
            });

            setStagesOptions(mbStages(sorted));
        }
    }, [allStagesDataResponse, stagesIsError, stagesLoading]);

    const { mutate: manageLead, isPending: isManageLeadPending } = useMutationRequestQuery(
        "manageLead",
        createCRMLeadService,
        {
            onSuccess: () => {
                toast.success("MB Builder client added successfully.", { position: "bottom-center" });
                navigate("/CRMTaskList");
            },
            onError: () => {
                toast.error("Failed to add MB Builder client.", { position: "bottom-center" });
            },
        }
    );

    const handleSubmit = (e: any) => {
        e.preventDefault();
        const errors: any = {};
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        const companyNameValue = formData.companyName?.trim() || "";
        const firstNameValue = formData.firstName?.trim() || "";
        const surnameValue = formData.surname?.trim() || "";
        const emailValue = formData.email?.trim() || "";
        const mobileValue = formData.mobile?.replace(/\s/g, "") || "";

        if (!companyNameValue) {
            errors.companyName = "Company name is required.";
        }

        if (!firstNameValue) {
            errors.firstName = "First name is required.";
        }

        if (!surnameValue) {
            errors.surname = "Surname is required.";
        }

        if (!emailValue) {
            errors.email = "Email is required.";
        } else if (!emailPattern.test(emailValue)) {
            errors.email = "Please enter a valid email address!";
        }

        if (!mobileValue) {
            errors.mobile = "Mobile number is required.";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors({});

        const payload: any = {
            ...formData,
            firstName: firstNameValue,
            surname: surnameValue,
            email: emailValue,
            companyName: companyNameValue,
            abn: formData.abn?.trim() || "",
            acn: formData.acn?.trim() || "",
        };

        manageLead(payload);
    };

    return (
        <>
            <div className="container-fluid">
                <div className="table-responsive h-100 py-4">
                    <section className="bg-white">
                        <div className="scrollable-table-container">
                            <table className="scrollable-table" style={{ marginLeft: "1%", marginTop: "3%" }}>
                                <div className="d-flex align-items-center">
                                    <Col xs={12} className="d-flex">
                                        <H3 className="heading-color f-w-900 text-left my-2">MB Builder</H3>
                                    </Col>
                                </div>

                                <div className="d-flex item-center mt-2">
                                    <Col className="responsive-col" xs={12} style={{ width: "10%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Company Name:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="companyName"
                                            value={formData.companyName}
                                            setValue={handleValueChange}
                                            placeholder="Company Name"
                                            design="flex"
                                            passCol="col-11"
                                            maxChars={100}
                                            tabIndex={1}
                                            required
                                            error={!!formErrors.companyName}
                                            errorMessage={formErrors.companyName || ""}
                                        />
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "7%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Source:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <DropdownWithValueName
                                            label=""
                                            name="source"
                                            value={formData.source}
                                            setValue={handleValueChange}
                                            options={leadDropdownData?.leadSources || []}
                                            passCol="col-9"
                                            design="flex"
                                            tabIndex={9}
                                        />
                                    </Col>
                                </div>

                                <div className="d-flex item-center">
                                    <Col className="responsive-col" xs={12} style={{ width: "10%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Company ABN:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="abn"
                                            value={formatAbn(formData.abn)}
                                            setValue={(name: string, value: any) => handleValueChange(name, formatAbn(String(value ?? "")))}
                                            placeholder="99 006 164 709"
                                            design="flex"
                                            passCol="col-11"
                                            maxChars={ABN_INPUT_MAX_LEN}
                                            tabIndex={2}
                                        />
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "7%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Staff:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <DropdownWithValueName
                                            label=""
                                            name="staff"
                                            value={formData.staff}
                                            setValue={handleValueChange}
                                            options={leadDropdownData?.staff || []}
                                            passCol="col-9"
                                            design="flex"
                                            tabIndex={10}
                                        />
                                    </Col>
                                </div>

                                <div className="d-flex item-center">
                                    <Col className="responsive-col" xs={12} style={{ width: "10%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Company ACN:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="acn"
                                            value={formatAcn(formData.acn)}
                                            setValue={(name: string, value: any) => handleValueChange(name, formatAcn(String(value ?? "")))}
                                            placeholder="006 164 709"
                                            design="flex"
                                            passCol="col-11"
                                            maxChars={ACN_INPUT_MAX_LEN}
                                            tabIndex={3}
                                        />
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "7%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Stage:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <DropdownWithValueName
                                            label=""
                                            name="stage"
                                            value={formData.stage}
                                            setValue={handleValueChange}
                                            options={stagesOptions}
                                            passCol="col-9"
                                            design="flex"
                                            tabIndex={11}
                                            placeholder="Select"
                                        />
                                    </Col>
                                </div>

                                <div className="d-flex item-center">
                                    <Col className="responsive-col" xs={12} style={{ width: "10%", paddingInline: "2px" }}>
                                        <Label className="mb-3">First Name:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            setValue={handleNameChange}
                                            placeholder="First Name"
                                            design="flex"
                                            passCol="col-11"
                                            maxChars={30}
                                            tabIndex={4}
                                            required
                                            error={!!formErrors.firstName}
                                            errorMessage={formErrors.firstName || ""}
                                        />
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "7%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Follow Up:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <SelectDatePicker
                                            label=""
                                            name="followUp"
                                            value={formData.followUp}
                                            setValue={handleValueChange}
                                            passCol="col-9"
                                            design="flex"
                                            tabIndex={12}
                                        />
                                    </Col>
                                </div>

                                <div className="d-flex item-center">
                                    <Col className="responsive-col" xs={12} style={{ width: "10%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Surname:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="surname"
                                            value={formData.surname}
                                            setValue={handleNameChange}
                                            placeholder="Surname"
                                            design="flex"
                                            passCol="col-11"
                                            maxChars={30}
                                            tabIndex={5}
                                            required
                                            error={!!formErrors.surname}
                                            errorMessage={formErrors.surname || ""}
                                        />
                                    </Col>
                                </div>

                                <div className="d-flex item-center">
                                    <Col className="responsive-col" xs={12} style={{ width: "10%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Contact Email:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <TextField
                                            label=""
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            setValue={handleValueChange}
                                            placeholder="Email"
                                            design="flex"
                                            passCol="col-11"
                                            maxChars={40}
                                            tabIndex={6}
                                            required
                                            error={!!formErrors.email}
                                            errorMessage={formErrors.email || ""}
                                        />
                                    </Col>
                                </div>

                                <div className="d-flex item-center">
                                    <Col className="responsive-col" xs={12} style={{ width: "10%", paddingInline: "2px" }}>
                                        <Label className="mb-3">Contact Mobile:</Label>
                                    </Col>
                                    <Col className="responsive-col" xs={12} style={{ width: "20%", paddingInline: "2px" }}>
                                        <MobileField
                                            label=""
                                            mobileField="mobile"
                                            value={formData.mobile}
                                            setValue={(field, value) => handleValueChange(field, value)}
                                            placeholder="Mobile"
                                            design="flex"
                                            passCol="col-9"
                                            maxLength={14}
                                            tabIndex={7}
                                            required
                                            error={!!formErrors.mobile}
                                            errorMessage={formErrors.mobile || ""}
                                        />
                                    </Col>
                                </div>

                                <div className="d-flex align-items-center">
                                    <Col xs={12} className="d-flex">
                                        <H3 className="heading-color f-w-900 text-left my-2">Lead Comments</H3>
                                    </Col>
                                </div>
                                <Col className="responsive-col" xs={12} style={{ width: "85%", paddingInline: "2px" }}>
                                    <TextField
                                        label=""
                                        type="textarea"
                                        name="CommentText"
                                        value={formData.CommentText}
                                        setValue={handleValueChange}
                                        placeholder="Enter lead comments here..."
                                        design="default"
                                        rows={5}
                                        tabIndex={10}
                                        maxChars={500}
                                    />
                                </Col>
                            </table>

                            <div className="d-flex gap-3 py-3">
                                <Button onClick={() => navigate("/CRMTaskList")} color="secondary">
                                    Back to Leads
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    color="primary"
                                    disabled={isManageLeadPending}
                                    type="submit"
                                >
                                    {isManageLeadPending ? "Loading . . ." : "Save Lead"}
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
            <ToastContainer />
        </>
    );
}
